import type { CheckoutScreeningQuestion } from "@fm/api/screening-questions-checkout";

/** Answers/errors keyed by the catalog question's integer id, as a string. */
export type ScreeningValues = Record<string, string>;
/** `undefined` clears a question's error (e.g. on the next edit) without a delete. */
export type ScreeningErrors = Record<string, string | undefined>;

const REQUIRED_MESSAGE = "This question is required.";
const RADIO_MESSAGE = "Choose one of the listed options.";

/**
 * Field-level errors, keyed by question id, or `{}` when the answers can be
 * sent. `required` and the `radio` options check both mirror the server's
 * own `Core.Fleet.ScreeningQuestions.validate_answers/3` (defense in depth —
 * the server re-validates regardless).
 */
export function validateScreeningAnswers(
  fields: CheckoutScreeningQuestion[],
  values: ScreeningValues,
): ScreeningErrors {
  const errors: ScreeningErrors = {};

  for (const field of fields) {
    const key = String(field.id);
    const text = (values[key] ?? "").trim();

    if (text === "") {
      if (field.required) errors[key] = REQUIRED_MESSAGE;
      continue;
    }

    if (field.type === "radio" && !field.options.includes(text)) {
      errors[key] = RADIO_MESSAGE;
    }
  }

  return errors;
}

export interface ScreeningPayload {
  screeningAnswers: Record<string, string>;
}

/**
 * Typed answers for `POST /soft-holds`, keyed by the catalog question's
 * integer id (as a string, since JSON object keys are always strings) — the
 * shape `Core.Fleet.ScreeningQuestions.validate_answers/3` expects. Blank
 * answers are left out entirely. The renter's free-text message to the
 * fleet is just another catalog question (a backfilled `textarea` question
 * labeled "Message to the fleet") and is answered through this same
 * payload — there is no separate `renter_message` field any more.
 */
export function buildScreeningPayload(
  fields: CheckoutScreeningQuestion[],
  values: ScreeningValues,
): ScreeningPayload {
  const screeningAnswers: ScreeningPayload["screeningAnswers"] = {};

  for (const field of fields) {
    const key = String(field.id);
    const text = (values[key] ?? "").trim();
    if (text === "") continue;
    screeningAnswers[key] = text;
  }

  return { screeningAnswers };
}
